import { Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrimeTemplate, TreeNode } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { OrganizationChart } from 'primeng/organizationchart';
import { EMPTY, expand, map, reduce } from 'rxjs';

import { UsersService } from '../../../core/services/users.service';
import { SkeletonBlockComponent } from '../../../shared/components/skeleton-block/skeleton-block.component';
import { capitalizeWords, formatNombreCompletoDisplay } from '../../../shared/helpers/nombre-texto.helper';
import { buildAvatarStyle, buildIniciales } from '../../../shared/helpers/map-congregacion-row';
import { UserListItem } from '../../../shared/interfaces/user.interface';
import {
  NodoOrganigrama,
  PersonaOrganigrama,
  armarOrganigrama,
} from './armar-organigrama';
import {
  OrganigramaAnimacionDirective,
  OrganigramaHorizontalDirective,
  OrganigramaNodoDirective,
} from './organigrama-horizontal';

const PAGE_SIZE = 100;

interface PersonaNodo {
  id: string;
  nombreVisible: string;
  ministerio: string;
  sinConexion: boolean;
  trinidad: boolean;
  iniciales: string;
  discipulos: number;
  avatarStyle: Record<string, string>;
}

interface PiramideVista {
  key: string;
  suelta: boolean;
  nodos: TreeNode<PersonaNodo>[];
}

interface PaginaUsuarios {
  page: number;
  data: UserListItem[];
  total: number;
  limit: number;
}

@Component({
  selector: 'app-organigrama',
  standalone: true,
  imports: [
    OrganizationChart,
    PrimeTemplate,
    Avatar,
    SkeletonBlockComponent,
    OrganigramaHorizontalDirective,
    OrganigramaNodoDirective,
    OrganigramaAnimacionDirective,
  ],
  templateUrl: './organigrama.component.html',
  styleUrl: './organigrama.component.scss',
})
export class OrganigramaComponent implements OnInit {
  private readonly usersService = inject(UsersService);
  private readonly destroyRef = inject(DestroyRef);

  readonly nombrePastor = input('');

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly sinCimas = signal(false);
  readonly piramides = signal<PiramideVista[]>([]);
  readonly fuera = signal(0);

  ngOnInit(): void {
    this.cargar();
  }

  private cargar(): void {
    this.loading.set(true);
    this.error.set(null);

    this.pedirPagina(1)
      .pipe(
        expand((pagina) =>
          pagina.page * pagina.limit < pagina.total && pagina.data.length > 0
            ? this.pedirPagina(pagina.page + 1)
            : EMPTY,
        ),
        reduce((todas, pagina) => todas.concat(pagina.data), [] as UserListItem[]),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (usuarios) => this.pintar(usuarios),
        error: () => {
          this.loading.set(false);
          this.error.set('No se pudo cargar el organigrama.');
        },
      });
  }

  private pedirPagina(page: number) {
    return this.usersService.list({ page, limit: PAGE_SIZE, sort: 'nombre:asc' }).pipe(
      map((respuesta): PaginaUsuarios => ({
        page,
        data: respuesta.data ?? [],
        total: respuesta.pagination?.total ?? respuesta.data?.length ?? 0,
        limit: respuesta.pagination?.limit || PAGE_SIZE,
      })),
    );
  }

  private pintar(usuarios: UserListItem[]): void {
    const armado = armarOrganigrama(usuarios.map(aPersona), this.nombrePastor());
    const piramides: PiramideVista[] = [];
    if (armado.validos.length > 0) {
      piramides.push({
        key: 'trinidad',
        suelta: false,
        nodos: [nodoTrinidad(armado.validos.map((cima) => aTree(cima)))],
      });
    }
    for (const suelto of armado.sueltos) {
      piramides.push({
        key: `suelto-${suelto.id}`,
        suelta: true,
        nodos: [aTree(suelto, true)],
      });
    }
    this.sinCimas.set(armado.validos.length === 0);
    this.piramides.set(piramides);
    this.fuera.set(armado.fuera);
    this.loading.set(false);
  }
}

function aPersona(usuario: UserListItem): PersonaOrganigrama {
  return {
    id: usuario.id ?? '',
    nombre: usuario.nombre ?? '',
    nombre2: usuario.nombre2 ?? '',
    apellido: usuario.apellido ?? '',
    lider: usuario.lider === true,
    idLider: usuario.idLider ?? '',
    ministerio: usuario.ministerio ?? '',
    sexo: usuario.sexo ?? '',
  };
}

function aTree(nodo: NodoOrganigrama, sinConexion = false): TreeNode<PersonaNodo> {
  const hijos = nodo.hijos.map((hijo) => aTree(hijo));
  return {
    key: nodo.id,
    type: 'persona',
    expanded: false,
    data: {
      id: nodo.id,
      nombreVisible: formatNombreCompletoDisplay(nodo.nombre, nodo.nombre2, nodo.apellido),
      ministerio: ministerioVisible(nodo.ministerio),
      sinConexion,
      trinidad: false,
      iniciales: buildIniciales(nodo.nombre, nodo.apellido),
      discipulos: nodo.hijos.length,
      avatarStyle: buildAvatarStyle(nodo.sexo),
    },
    children: hijos.length > 0 ? hijos : undefined,
  };
}

function nodoTrinidad(cimas: TreeNode<PersonaNodo>[]): TreeNode<PersonaNodo> {
  return {
    key: 'trinidad',
    type: 'persona',
    expanded: true,
    data: {
      id: 'trinidad',
      nombreVisible: 'Padre, Hijo y Espíritu Santo',
      ministerio: '',
      sinConexion: false,
      trinidad: true,
      iniciales: '',
      discipulos: cimas.length,
      avatarStyle: {},
    },
    children: cimas,
  };
}

function ministerioVisible(valor: string): string {
  const texto = valor.trim();
  if (!texto || texto.toLowerCase() === 'ninguno') {
    return '';
  }
  return capitalizeWords(texto);
}
